import { useState, useEffect } from 'react';

/**
 * Custom hook to detect media query matches
 * @param {string} query - Media query string (e.g., '(min-width: 1024px)')
 * @returns {boolean} - Whether the media query matches
 */
export const useMediaQuery = (query) => {
    // Check immediately to prevent flickering - use a function to get initial value
    const getInitialValue = () => {
        // Check if window is available (client-side)
        if (typeof window !== 'undefined') {
            return window.matchMedia(query).matches;
        }
        // For SSR, default to desktop (most common)
        return query.includes('min-width: 1024px');
    };

    const [matches, setMatches] = useState(getInitialValue);

    useEffect(() => {
        const media = window.matchMedia(query);

        // Update to current value (should already match initial, but ensures sync)
        setMatches(media.matches);

        // Create listener for changes
        const listener = (e) => setMatches(e.matches);

        // Add listener
        media.addEventListener('change', listener);

        // Cleanup
        return () => media.removeEventListener('change', listener);
    }, [query]);

    return matches;
};

// Predefined breakpoint hooks
export const useIsMobile = () => useMediaQuery('(max-width: 767px)');
export const useIsTablet = () => useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
export const useIsDesktop = () => useMediaQuery('(min-width: 1024px)');
export const useIsLargeScreen = () => useMediaQuery('(min-width: 1280px)');
